<template>
  <div class="component-wrapper inner d-flex flex-column align-center">
    <v-btn icon color="primary" @click="onBack" class="close-button">
      <v-icon>mdi-close</v-icon>
    </v-btn>

    <div class="body-2 text-center mb-2" v-if="role == 'superuser'">
      ({{ activeMuseum.defaultTitle }})
    </div>

    <div class="text-h6 primary--text text-center mb-1">{{ $t('step1') }}</div>

    <div class="text-h6 text-center title-underline mb-12">
      {{ $t('programDetails') }}
    </div>

    <div class="inner-wrapper d-flex flex-column">
      <v-text-field
        outlined
        :label="$t('title')"
        v-model="title"
        :error-messages="titleErrors"
        class="mb-2"
      ></v-text-field>

      <div v-if="isCopy" class="unique-code-message">
        Please enter the unique code of the program
      </div>
      <v-text-field
        outlined
        :label="$t('uniqueCode')"
        v-model="uniqueCode"
        :error-messages="uniqueCodeErrors"
        :disabled="role == 'admin'"
        class="mb-2"
        @input="onInput"
      ></v-text-field>

      <v-btn
        v-if="isEdit"
        color="primary"
        class="align-self-center"
        :loading="saveLoader"
        :disabled="!hasChanges"
        :lang="$i18n.locale"
        @click="newOrEditProgram()"
      >
        <v-icon left>mdi-content-save</v-icon>
        {{ $t('save') }}
      </v-btn>

      <v-divider class="my-10"></v-divider>

      <div class="d-flex">
        <v-btn
          color="primary"
          small
          disabled
          :lang="$i18n.locale"
        >
          <v-icon left>mdi-chevron-left</v-icon>
          {{ $t('previous') }}
        </v-btn>

        <v-btn
          class="ml-auto"
          color="primary"
          small
          :loading="loader"
          :disabled="hasChanges && isEdit"
          :lang="$i18n.locale"
          @click="newOrEditProgram(isEdit)"
        >
          {{ $t('next') }}
          <v-icon right>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>

    <v-dialog v-model="confirmDialog" max-width="450">
      <div class="white pa-3">
        <div class="body-1 text-center mb-4">
          {{ $t('unsavedChanges') }}
        </div>

        <div class="d-flex justify-center">
          <v-btn
            color="error"
            small
            class="mr-2"
            :lang="$i18n.locale"
            @click="
              forceLeave = true;
              $router.push({ path: pathToLeave });
            "
          >
            {{ $t('continue') }}
          </v-btn>

          <v-btn
            color="primary"
            small
            :lang="$i18n.locale"
            @click="confirmDialog = false"
          >
            {{ $t('stay') }}
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import { mapActions, mapMutations, mapState } from "vuex";

const uniqueTitleValidator = function () {
  return !this.titleExists;
};

const uniqueCodeValidator = function () {
  return !this.uniqueCodeExists;
};

export default {
  data() {
    return {
      title: "",
      uniqueCode: "",
      titleExists: false,
      uniqueCodeExists: false,
      loader: false,
      saveLoader: false,
      initialData: null,
      confirmDialog: false,
      forceLeave: false,
      pathToLeave: null,
    };
  },
  validations: {
    title: { required, uniqueTitleValidator },
    uniqueCode: { required, uniqueCodeValidator },
  },
  computed: {
    ...mapState({
      currentProgram: (state) => state.admin.currentProgram,
      allowStep2: (state) => state.admin.allowStep2,
      role: (state) => state.auth.role,
      activeMuseum: (state) => state.superuser.activeMuseum,
    }),
    isEdit() {
      return this.$route.path.includes("edit");
    },
    titleErrors() {
      const errors = [];
      if (!this.$v.title.$dirty) return errors;
      if (!this.$v.title.required) errors.push(this.$t('titleRequired'));
      if (!this.$v.title.uniqueTitleValidator)
        errors.push(this.$t('titleInUse'));
      return errors;
    },
    uniqueCodeErrors() {
      const errors = [];
      if (!this.$v.uniqueCode.$dirty) return errors;
      if (!this.$v.uniqueCode.required)
        errors.push(this.$t('uniqueCodeRequired'));
      if (!this.$v.uniqueCode.uniqueCodeValidator)
        errors.push(this.$t('uniqueCodeInUse'));
      return errors;
    },
    hasChanges() {
      return (
        JSON.stringify(this.initialData) !==
        JSON.stringify({
          title: this.title,
          uniqueCode: this.uniqueCode,
        })
      );
    },
    isCopy() {
      return this.currentProgram?.uniqueCode ? this.currentProgram.uniqueCode.includes('_copy') : false;
    }
  },
  created() {
    this.title = this.currentProgram?.defaultTitle;
    this.uniqueCode = this.currentProgram?.uniqueCode;

    this.initialData = {
      title: this.title,
      uniqueCode: this.uniqueCode,
    };
  },
  methods: {
    ...mapActions(["newProgram", "editProgram", "getPrograms"]),
    async newOrEditProgram(next = false) {
      this.titleExists = false;
      this.uniqueCodeExists = false;

      this.$v.$touch();
      if (this.$v.$invalid) return;

      if (!this.hasChanges) {
        if (this.isEdit) {
          if (this.role == "admin") {
            this.$router.push({
              path: `/program/edit/${this.currentProgram.uniqueCode}/step2`,
            });
          } else {
            this.$router.push({
              path: `/superuser/museum/${this.activeMuseum.id}/program/edit/${this.currentProgram.uniqueCode}/step2`,
            });
          }
        } else {
          if (this.role == "admin") {
            this.$router.push({ path: "/program/new/step2" });
          } else {
            this.$router.push({
              path: `/superuser/museum/${this.activeMuseum.id}/program/new/step2`,
            });
          }
        }
        return;
      }

      this.isEdit ? (this.saveLoader = true) : (this.loader = true);

      try {
        if (!this.isEdit && !this.allowStep2) {
          let data = {
            defaultLocale: "en",
            defaultTitle: this.title,
            uniqueCode: this.uniqueCode,
            mainPhoto: "",
            active: true,
            draft: false,
            testingMode: true,
            remainingTestingTimeInSeconds: 1800,
            museumId: this.activeMuseum.id,
            description: '',
            locales: [
              {
                locale: "en",
                title: this.title
              }
            ]
          };
          await this.newProgram(data);
          this.loader = false;

          this.title = this.currentProgram.defaultTitle;
          this.uniqueCode = this.currentProgram.uniqueCode;

          this.initialData = {
            title: this.title,
            uniqueCode: this.uniqueCode,
          };

          if (this.role == "admin") {
            this.$router.push({ path: "/program/new/step2" });
          } else {
            this.$router.push({
              path: `/superuser/museum/${this.activeMuseum.id}/program/new/step2`,
            });
          }
        } else {
          await this.editProgram({
            programId: this.currentProgram.id,
            uniqueCode: this.uniqueCode,
            defaultLocale: this.currentProgram.defaultLocale,
            defaultTitle: this.title,
            title: this.title,
          });

          await this.getPrograms();

          this.title = this.currentProgram.defaultTitle;
          this.uniqueCode = this.currentProgram.uniqueCode;

          this.initialData = {
            title: this.title,
            uniqueCode: this.uniqueCode,
          };

          this.saveLoader = false;

          if (!this.isEdit) {
            this.loader = false;

            if (this.role == "admin") {
              this.$router.push({ path: "/program/new/step2" });
            } else {
              this.$router.push({
                path: `/superuser/museum/${this.activeMuseum.id}/program/new/step2`,
              });
            }
          }
        }
      } catch (e) {
        if (e?.response?.status == 400) {
          if (e.response?.data?.data?.uniqueCode) {
            this.uniqueCodeExists = true;
          }

          if (e.response?.data?.data?.title) {
            this.titleExists = true;
          }
        }
        this.loader = false;
        this.saveLoader = false;
      }
    },
    onBack() {
      if (this.role == "admin") {
        this.$router.push({ path: "/" });
      } else {
        this.$router.push({
          path: `/superuser/museum/${this.activeMuseum.id}`,
        });
      }
    },
    onInput(val) {
      this.uniqueCode = val.toUpperCase();
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasChanges && !this.forceLeave) {
      this.pathToLeave = to.path;
      this.confirmDialog = true;
    } else {
      next();
    }
  },
};
</script>

<style lang="scss" scoped>
  .unique-code-message {
    margin-bottom: 12px;
    color: #ff5252;
    font-size: 12px;
  }
</style>
